'use client'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBookingCart } from '~/stores/booking-cart'
import { useHotelAvailability } from '~/hooks/use-hotel-availability'
import { useHotels } from '~/hooks/use-hotels'
import { BookingStep, useBookingFlow } from '~/stores/booking-flow'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { useHotelAvailabilityStore } from '~/stores/hotel-availability'

const Header = dynamic(() => import('~/components/sections/header'), {
  ssr: false,
  // loading: () => <HeaderSkeleton />
})
const BookingSearch = dynamic(() => import('../search'), {
  ssr: false,
  loading: () => <SearchSkeleton />,
})
const BookingListDesktop = dynamic(() => import('../list'), {
  ssr: false,
  loading: () => <ListSkeleton />,
})
const BookingSummary = dynamic(() => import('../list/booking-summary'), {
  ssr: false,
  loading: () => <BookingSummarySkeleton />,
})

import SearchSkeleton from '../search/SearchSkeleton'
import ListSkeleton from './ListSkeleton'
import BookingSummarySkeleton from '../list/booking-summary-skeleton'
// import Header from '~/components/sections/header'
import { Footer } from '~/components/sections/footer'
import DeleteConfirm from '../list/delete-confim'
import { useLocale } from '~/contexts/LocaleContext'
import { DEFAULT_LANGUAGE } from '~/data/language'

export default function BookingSearchListDesktop({
  setTimeIn,
  setTimeOut,
}: {
  setTimeIn: (time: string) => void
  setTimeOut: (time: string) => void
}) {
  const { hotels, isLoading: isLoadingHotels } = useHotels()
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const {
    selectedHotel,
    checkInDate,
    checkOutDate,
    adults,
    kidsOver6,
    kidsUnder6,
    setCurrentStep,
    setSearchData,
    addRoom,
    clearBookingData,
  } = useBookingFlow()
  const urlHotelId = searchParams.get('hotelId')
  const urlCheckInDate = searchParams.get('checkInDate')
  const urlCheckOutDate = searchParams.get('checkOutDate')
  const urlAdults = searchParams.get('adults')
  const urlKidsOver6 = searchParams.get('kidsOver6')
  const urlKidsUnder6 = searchParams.get('kidsUnder6')
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const todayString = today.toLocaleDateString('en-CA')
  const tomorrowString = tomorrow.toLocaleDateString('en-CA')
  const hasPastDate =
    (urlCheckInDate && urlCheckInDate < todayString) ||
    (urlCheckOutDate && urlCheckOutDate < todayString)
  const selectedCheckInDate = hasPastDate ? todayString : urlCheckInDate || checkInDate
  const selectedCheckOutDate = hasPastDate ? tomorrowString : urlCheckOutDate || checkOutDate
  const { removeFromCart } = useBookingCart()
  const {
    items: cartItems,
    updateAdults,
    updateChildren6To12,
    updateChildrenUnder6,
    clearCart,
  } = useBookingCart()
  const { locale } = useLocale()
  const { availability } = useHotelAvailabilityStore()
  const availabilityParams = useMemo(
    () => ({
      hotelId: urlHotelId || selectedHotel,
      fromDate: selectedCheckInDate,
      toDate: selectedCheckOutDate,
      adults: Number(urlAdults) || adults,
      children: (Number(urlKidsOver6) || kidsOver6) + (Number(urlKidsUnder6) || kidsUnder6),
    }),
    [
      urlHotelId,
      selectedHotel,
      selectedCheckInDate,
      selectedCheckOutDate,
      urlAdults,
      adults,
      urlKidsOver6,
      kidsOver6,
      urlKidsUnder6,
      kidsUnder6,
    ]
  )
  const { refetch } = useHotelAvailability(availabilityParams)

  const [editingRoomId, setEditingRoomId] = useState<string | null>(null)
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null)
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (!hasPastDate) return

    const params = new URLSearchParams(searchParams.toString())
    params.set('checkInDate', todayString)
    params.set('checkOutDate', tomorrowString)
    router.replace(`${pathname}?${params.toString()}`, { scroll: false })
  }, [hasPastDate, pathname, router, searchParams, todayString, tomorrowString])

  const updateURLParams = useCallback(
    (data: any) => {
      const params = new URLSearchParams()
      params.set('hotelId', data.hotelId)
      params.set('checkInDate', data.checkInDate)
      params.set('checkOutDate', data.checkOutDate)
      params.set('adults', data.adults.toString())
      params.set('kidsOver6', data.kidsOver6.toString())
      params.set('kidsUnder6', data.kidsUnder6.toString())
      // Remove roomType param when searching
      // if (data.roomType) {
      //   params.set('roomType', data.roomType)
      // }
      // Use pathname to determine if we need to include locale
      const shouldIncludeLocale =
        (pathname !== '/' && pathname?.startsWith(`/${locale || DEFAULT_LANGUAGE}`)) ||
        pathname === '/'
      const basePath = shouldIncludeLocale ? `/${locale}/booking` : '/booking'
      router.push(`${basePath}?${params.toString()}`, { scroll: false })
    },
    [router, locale, pathname]
  )
  const handleSearchSubmit = useCallback(
    async (data: any) => {
      try {
        clearBookingData()
        clearCart()
        setSearchData(data)
        // Cập nhật URL sau khi đã set data mới
        updateURLParams(data)
        // Đợi một tick để đảm bảo data đã được cập nhật
        await refetch({
          hotelId: data.hotelId,
          fromDate: data.checkInDate,
          toDate: data.checkOutDate,
          adults: data.adults,
          children: data.kidsOver6 + data.kidsUnder6,
        })
        setSearching(!searching)
      } catch (error) {
        console.error('Error refetching availability:', error)
      }
    },
    [updateURLParams, setSearchData, clearBookingData, clearCart, setSearching, searching]
  )

  const handleRoomSelect = useCallback(
    (room: any) => {
      addRoom(room)
    },
    [addRoom]
  )

  const validRoomTypes = useMemo(() => {
    return availability?.filter((room) => room.available > 0) || []
  }, [availability])
  useEffect(() => {
    setTimeIn(validRoomTypes[0]?.hotel.checkInTime || '14:00')
    setTimeOut(validRoomTypes[0]?.hotel.checkOutTime || '12:00')
  }, [validRoomTypes, setTimeIn, setTimeOut])
  const bookingRooms = useMemo(() => {
    return cartItems.map((item) => ({
      cartItemId: item.cartItemId,
      id: item.roomId,
      name: item.roomName,
      imageUrl:
        validRoomTypes.find((r) => r.roomId === item.roomId)?.roomInfo?.imageUrl ||
        validRoomTypes.find((r) => r.roomId === item.roomId)?.roomInfo?.images[0]?.imageUrl ||
        '',
      view: validRoomTypes.find((r) => r.roomId === item.roomId)?.roomInfo?.roomView || '',
      quantity: 1,
      price: item.price,
      amount: item.amount,
      nights: 1,
      adults: item.guests.adults,
      children6To12: item.guests.children6To12,
      childrenUnder6: item.guests.childrenUnder6,
      maxAdults: item.maxAdults,
      maxChildren: item.maxChildren,
      totalPrice: item.totalPrice,
      extraGuestDesc: item.extraGuestDesc,
    }))
  }, [cartItems, validRoomTypes])

  const handleGuestChange = (
    cartItemId: string,
    type: 'adults' | 'children6To12' | 'childrenUnder6',
    value: number
  ) => {
    const room = bookingRooms.find((r) => r.cartItemId === cartItemId)
    if (!room) return

    switch (type) {
      case 'adults':
        updateAdults(cartItemId, value)
        break
      case 'children6To12':
        updateChildren6To12(cartItemId, value)
        break
      case 'childrenUnder6':
        updateChildrenUnder6(cartItemId, value)
        break
    }
  }
  const onDeleteCard = (cartItemId: string) => {
    setDeletingCardId(cartItemId)
  }
  const handleDeleteConfirm = () => {
    if (deletingCardId) {
      removeFromCart(deletingCardId)
      setDeletingCardId(null)
      setShowDeleteConfirm(false)
    }
  }
  return (
    <>
      <Header />
      <div className="mx-auto bg-[#F3F3F3]">
        <BookingSearch
          hotels={hotels}
          onSubmit={handleSearchSubmit}
          initialValues={{
            hotelId: searchParams.get('hotelId') || selectedHotel,
            checkInDate: selectedCheckInDate,
            checkOutDate: selectedCheckOutDate,
            adults: parseInt(searchParams.get('adults') || '1', 10),
            kidsOver6: parseInt(searchParams.get('kidsOver6') || '0', 10),
            kidsUnder6: parseInt(searchParams.get('kidsUnder6') || '0', 10),
            roomType: searchParams.get('roomType') || '',
          }}
        />

        <div className="bg-[#F3F3F3] pb-6">
          <div className="max-w-container mx-auto">
            <div className="md:mx-5 lg:mx-10">
              <div className="flex flex-col gap-6 lg:flex-row">
                {/* Left: Room List */}
                <div className="w-full lg:w-[70%]">
                  <BookingListDesktop
                    hotelId={selectedHotel}
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    adults={adults}
                    kidsOver6={kidsOver6}
                    kidsUnder6={kidsUnder6}
                    roomType={searchParams.get('roomType') || ''}
                    onRoomSelect={handleRoomSelect}
                    onBackToSearch={() => {
                      const url = new URL(window.location.href)
                      url.searchParams.delete('step')
                      window.history.replaceState({}, '', url)
                      setCurrentStep(BookingStep.SEARCH)
                    }}
                    onContinue={() => {
                      const url = new URL(window.location.href)
                      url.searchParams.delete('ref')
                      url.searchParams.set('step', '1')
                      window.history.replaceState({}, '', url)
                      setCurrentStep(BookingStep.PAYMENT)
                    }}
                    setTimeIn={setTimeIn}
                    setTimeOut={setTimeOut}
                    searching={searching}
                  />
                </div>

                {/* Right: Booking Summary */}
                <div className="w-full lg:w-[30%]">
                  <div className="lg:sticky lg:top-[100px]">
                    <BookingSummary
                      setEditingRoomId={setEditingRoomId}
                      editingRoomId={editingRoomId}
                      onContinue={() => {
                        const url = new URL(window.location.href)
                        url.searchParams.delete('ref')
                        url.searchParams.set('step', '1')
                        window.history.replaceState({}, '', url)
                        setCurrentStep(BookingStep.PAYMENT)
                      }}
                      checkIn={checkInDate}
                      checkOut={checkOutDate}
                      checkInTime={validRoomTypes[0]?.hotel.checkInTime || ''}
                      checkOutTime={validRoomTypes[0]?.hotel.checkOutTime || ''}
                      totalRooms={cartItems.length}
                      rooms={bookingRooms}
                      handleGuestChange={handleGuestChange}
                      onDelete={onDeleteCard}
                      setShowDeleteConfirm={setShowDeleteConfirm}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <DeleteConfirm
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => handleDeleteConfirm()}
      />
    </>
  )
}
